import {
  AugmentedCreateCltTrustInput,
  AugmentedCreateCrtTrustInput,
  AugmentedCreateDonorAdvisedFundInput,
  AugmentedCreateEntityInput,
  AugmentedCreateGratTrustInput,
  AugmentedCreateIlitTrustInput,
  AugmentedCreateIndividualPersonalAccountInput,
  AugmentedCreateIrrevocableTrustInput,
  AugmentedCreateJointPersonalAccountInput,
  AugmentedCreatePrincipalInput,
  AugmentedCreatePrivateFoundationInput,
  AugmentedCreateQprtTrustInput,
  AugmentedCreateQualifiedTuitionPersonalAccountInput,
  AugmentedCreateRetirementPersonalAccountInput,
  AugmentedCreateRevocableTrustInput,
  AugmentedCreateSlatTrustInput,
  CreateEntityInput,
  EntityKind,
  EntityStage,
  Exact,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import {
  entityGstStatusToApiTypeMap,
  estateInclusionStatusToApiTypeMap,
  nonTrustEntityTaxStatusToApiTypeMap,
  trustTaxStatusToApiTypeMap,
} from '../EntitySubforms/utils/shared/taxStatus.utils';
import { GenericTrustTaxStatus } from '../TaxStatusSubform/TaxStatusSubform.types';
import { getTaxStatusValuesFromEntityKind } from '../TaxStatusSubform/TaxStatusSubform.utils';
import { HypotheticalDraftEntityType } from './draftEntities.constants';

interface GetCreateDraftEntityInputParams {
  draftEntityType: HypotheticalDraftEntityType;
  draftEntityName: string;
  draftEntityGrantorId: string;
  draftEntityTaxStatus?: GenericTrustTaxStatus | '';
  householdId: string;
}

function getDefaultHypotheticalEmptyAccount(householdId: string) {
  return {
    create: {
      displayName: 'Default account',
      householdID: householdId,
    },
    withInitialValuation: null,
    withValuations: [],
  };
}

export function getCreateDraftEntityInput({
  draftEntityType,
  draftEntityName,
  draftEntityGrantorId,
  draftEntityTaxStatus,
  householdId,
}: GetCreateDraftEntityInputParams): AugmentedCreateEntityInput {
  const create: CreateEntityInput = {
    stage: EntityStage.Draft,
    householdID: householdId,
  };

  const principals: AugmentedCreatePrincipalInput[] = draftEntityGrantorId
    .split(',')
    .map((id) => ({
      create: {
        individualID: id,
        householdID: householdId,
      },
    }));

  const taxStatusValues = getTaxStatusValuesFromEntityKind(draftEntityType);

  const selectedTrustTaxStatus =
    draftEntityTaxStatus || taxStatusValues.taxStatus;
  const trustTaxStatus = selectedTrustTaxStatus
    ? trustTaxStatusToApiTypeMap[selectedTrustTaxStatus]
    : undefined;
  const nonTrustEntityTaxStatus = taxStatusValues.nonTrustEntityTaxStatus
    ? nonTrustEntityTaxStatusToApiTypeMap[
        taxStatusValues.nonTrustEntityTaxStatus
      ]
    : undefined;

  const inEstateStatus =
    estateInclusionStatusToApiTypeMap[taxStatusValues.estateInclusionStatus];

  const gstStatus = taxStatusValues.gstStatus
    ? entityGstStatusToApiTypeMap[taxStatusValues.gstStatus]
    : undefined;

  const createTrust: Exact<
    | AugmentedCreateIrrevocableTrustInput
    | AugmentedCreateRevocableTrustInput
    | AugmentedCreateSlatTrustInput
    | AugmentedCreateIlitTrustInput
  > = {
    create: {
      displayName: draftEntityName,
      taxStatus: trustTaxStatus,
      inEstateStatus,
      gstStatus,
      householdID: householdId,
    },
    withDesignerAccount: getDefaultHypotheticalEmptyAccount(householdId),
  };

  // This should be the same as `createTrust`, except its type makes householdID required.
  // Once all entity types require houshold IDs, this can go away
  const createTrustWithRequiredHouseholdID: Exact<
    | AugmentedCreateGratTrustInput
    | AugmentedCreateQprtTrustInput
    | AugmentedCreateCrtTrustInput
    | AugmentedCreateCltTrustInput
  > = {
    create: {
      displayName: draftEntityName,
      taxStatus: trustTaxStatus,
      inEstateStatus,
      gstStatus,
      householdID: householdId,
    },
    withDesignerAccount: getDefaultHypotheticalEmptyAccount(householdId),
  };

  const createNonTrust: Exact<
    | AugmentedCreateIndividualPersonalAccountInput
    | AugmentedCreateJointPersonalAccountInput
    | AugmentedCreateRetirementPersonalAccountInput
    | AugmentedCreateQualifiedTuitionPersonalAccountInput
    | AugmentedCreateDonorAdvisedFundInput
    | AugmentedCreatePrivateFoundationInput
  > = {
    create: {
      displayName: draftEntityName,
      taxStatus: nonTrustEntityTaxStatus,
      inEstateStatus,
      gstStatus,
      householdID: householdId,
    },
  };

  // This should be the same as `createNonTrust`, except its type makes householdID required.
  // Once all entity types require houshold IDs, this can go away
  const createNonTrustWithRequiredHouseholdID: Exact<
    AugmentedCreateDonorAdvisedFundInput | AugmentedCreatePrivateFoundationInput
  > = {
    create: {
      displayName: draftEntityName,
      taxStatus: nonTrustEntityTaxStatus,
      inEstateStatus,
      gstStatus,
      householdID: householdId,
    },
  };

  switch (draftEntityType) {
    case EntityKind.IrrevocableTrust:
      return {
        create,
        withIrrevocableTrust: {
          ...createTrust,
          withGrantors: principals,
        },
      };

    case EntityKind.RevocableTrust:
      return {
        create,
        withRevocableTrust: {
          ...createTrust,
          withGrantors: principals,
        },
      };

    case EntityKind.SlatTrust:
      return {
        create,
        withSlatTrust: {
          ...createTrust,
          withGrantor: principals[0],
        },
      };

    case EntityKind.GratTrust:
      return {
        create,
        withGratTrust: {
          ...createTrustWithRequiredHouseholdID,
          withGrantor: principals[0],
        },
      };

    case EntityKind.IlitTrust:
      return {
        create,
        withIlitTrust: {
          ...createTrust,
          withGrantors: principals,
        },
      };

    case EntityKind.QprtTrust:
      return {
        create,
        withQprtTrust: {
          ...createTrustWithRequiredHouseholdID,
          withGrantors: principals,
        },
      };

    case EntityKind.CrtTrust:
      return {
        create,
        withCrtTrust: {
          ...createTrustWithRequiredHouseholdID,
          withGrantors: principals,
        },
      };

    case EntityKind.CltTrust:
      return {
        create,
        withCltTrust: {
          ...createTrustWithRequiredHouseholdID,
          withGrantors: principals,
        },
      };

    case EntityKind.IndividualPersonalAccount:
      return {
        create,
        withIndividualPersonalAccount: {
          ...createNonTrust,
          withOwner: principals[0],
        },
      };

    case EntityKind.JointPersonalAccount:
      return {
        create,
        withJointPersonalAccount: {
          create: {
            ...createNonTrust.create,
            type: undefined,
            householdID: householdId,
          },
          withOwners: principals,
        },
      };

    case EntityKind.RetirementPersonalAccount:
      return {
        create,
        withRetirementPersonalAccount: {
          create: {
            ...createNonTrust.create,
            type: undefined,
            householdID: householdId,
          },
          withOwner: principals[0],
        },
      };

    case EntityKind.QualifiedTuitionPersonalAccount:
      return {
        create,
        withQualifiedTuitionPersonalAccount: {
          ...createNonTrust,
          withOwner: principals[0],
        },
      };

    case EntityKind.DonorAdvisedFund:
      return {
        create,
        withDonorAdvisedFund: {
          ...createNonTrustWithRequiredHouseholdID,
          withDonors: principals,
        },
      };

    case EntityKind.PrivateFoundation:
      return {
        create,
        withPrivateFoundation: {
          ...createNonTrustWithRequiredHouseholdID,
          withDonors: principals,
        },
      };

    default:
      throw new UnreachableError({
        case: draftEntityType,
        message: `Unrecognized entity type: ${draftEntityType}`,
      });
  }
}
